/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidatorFn } from '@angular/forms';
import { formatAsString } from 'app/shared/helpers/date-helpers';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import moment from 'moment';

const customValueProvider = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DatetimePickerComponent),
	multi: true,
};
const customValidateProvider = {
	provide: NG_VALIDATORS,
	useExisting: forwardRef(() => DatetimePickerComponent),
	multi: true,
};

@Component({
	selector: 'date-time-picker',
	templateUrl: './date-time-picker.component.html',
	styleUrls: ['./date-time-picker.component.scss'],
	providers: [customValueProvider, customValidateProvider],
	standalone: false,
})
export class DatetimePickerComponent implements ControlValueAccessor, OnChanges {
	dataForm: FormGroup;
	@Input() startTabIndex = 1;
	@Input() label: string = 'Date and Time';
	@Input() isValid: boolean = true;
	@Input() isDisabled: boolean = false;

	writtenValue: Date;

	constructor(fb: FormBuilder, private formHelperService: ReactiveFormHelperService) {
		this.dataForm = fb.group({
			date: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator()]],
			time: [{ value: '', disabled: this.isDisabled }, [this.isValidValidator()]],
		});

		this.dataForm.valueChanges.subscribe(() => this.onFormChanged());
	}

	// When the invalid input changes refresh the validity of the date and time controls and check for disabled
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.isDisabled) {
			if (this.isDisabled) {
				this.dataForm.get('date')?.disable();
				this.dataForm.get('time')?.disable();
			} else {
				this.dataForm.get('date')?.enable();
				this.dataForm.get('time')?.enable();
			}
		}
		if (changes.isValid) {
			this.date.updateValueAndValidity();
			this.time.updateValueAndValidity();
		}
	}

	/**
	 * Can be called when save is clicked on the parent form to mark child controls as touched and update validaiton.
	 * This appears to be necessary to resolve an issue in which the red box outline wasn't appearing if save is clicked before touching the controls.
	 */
	markAsTouchedAndUpdateValueAndValidity() {
		this.date.markAsTouched();
		this.time.markAsTouched();
		this.date.updateValueAndValidity();
		this.time.updateValueAndValidity();
	}

	markAsUntouched() {
		this.date.markAsUntouched();
		this.time.markAsUntouched();
	}

	get date(): AbstractControl {
		return this.dataForm.controls['date'];
	}
	get time(): AbstractControl {
		return this.dataForm.controls['time'];
	}

	// required to propogate the changes to the consuming form
	onFormChanged() {
		let combinedDate: Date;
		var datePart = typeof this.date.value === 'string' ? this.date.value : this.date.value?.format('YYYY-MM-DD');
		var timePart = this.time.value;
		if (datePart == '' || datePart == 'Invalid date') datePart = null;
		if (timePart == '' || timePart == 'Invalid date') timePart = null;
		if (datePart && timePart) {
			const dateTime = `${datePart}T${timePart}`;
			combinedDate = moment(dateTime).toDate();
			// next 2 lines commented as we don't want only the date to be filled-in, see KD0005625
			//  } else if (datePart) {
			//  combinedDate = moment(datePart).toDate();
			// next 2 lines commented as we typically don't want to allow only the time to be filled-in, and the below has the effect of assuming the current date.
			//		} else if (timePart) {
			//			combinedDate = moment(timePart, 'HH:mm').toDate();
		}

		console.log(`onFormChanged: ${combinedDate}`);

		this.propagateChange(combinedDate);
		this.onTouched();
	}

	propagateChange: any = () => {};
	onTouched: any = () => {
		console.log('touched');
	};

	writeValue(date: Date): void {
		this.writtenValue = date;
		this.setControlsFromWrittenValue();
		this.date.markAsPristine();
		this.time.markAsPristine();
	}

	private setControlsFromWrittenValue() {
		const dd = moment(this.writtenValue);
		const onSaleDate = dd.format('YYYY-MM-DD');
		const onSaleTime = dd.format('HH:mm');

		this.date.setValue(onSaleDate);
		this.time.setValue(onSaleTime);
		this.date.markAsPristine();
		this.time.markAsPristine();
	}

	registerOnChange(fn: any): void {
		this.propagateChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public validate(control: FormControl) {
		return null;
	}

	isValidValidator(): ValidatorFn {
		return (group: FormGroup): { [key: string]: any } => {
			return !this.isValid ? { invalid: true } : null;
		};
	}

	// Marks the control as pristine if the value has not changed from the original value.
	// This method should be called from the parent control 'cancel' method to prevent the 'confirmation dialog' from appearing
	markAsPristineIfUnchanged(control: AbstractControl, originalValue: Date) {
		const originalEventDateAndTime = formatAsString(moment(originalValue), 'YYYY-MM-DDTHH:mm:ss');
		var currentEventDateAndTime = formatAsString(control.value, 'YYYY-MM-DDTHH:mm:ss');

		var dateInvalid = this.date.value === null || this.date.value === 'Invalid date';
		var timeInvalid = this.time.value === null || this.time.value === 'Invalid date';
		var bothInvalid = dateInvalid && timeInvalid;

		// If both are null, we consider them unchanged, 'undefined' is the same as 'null' in this context
		// both invalid is being checked here because when only one part of the date/time is entered, the control value is null
		// and this is causing the cancel confirmation dialog to not appear when only one part of the date/time is changed.
		var bothAreNull = originalValue == null && control.value == null && bothInvalid;

		if (currentEventDateAndTime == originalEventDateAndTime || bothAreNull) {
			control.markAsPristine();
		}
	}

	markAsTouched() {
		this.onTouched();
	}
}
